import { client, gql } from 'data-provider';
import { IWarehouse } from 'interfaces';

interface GetAllWarehouseParam {
  searchString?: string;
  start?: number;
  limit?: number;
}

export type IWarehouseDropDown = Omit<
  IWarehouse,
  'address' | 'pointOfContacts' | 'totalStockValue' | 'email' | 'warehouseCode' | 'mobileNumber'
>;

export const getWarehouses = async (queryParam?: GetAllWarehouseParam) => {
  const where = { query: '' };
  if (queryParam?.searchString) {
    where.query = queryParam.searchString;
  }

  const query = gql`
    query ($where: JSON, $sort: SortInput, $start: Int, $limit: Int) {
      warehouses(where: $where, sort: $sort, start: $start, limit: $limit) {
        data {
          id
          name
          email
          warehouseCode
          mobileNumber
          totalStockValue
          address {
            street
            area
            city
            country
            zip
          }
        }
        total
        size
        page
      }
    }
  `;

  const variables = {
    where,
    start: queryParam && queryParam.start ? queryParam.start : 1,
    limit: queryParam && queryParam.limit ? queryParam.limit : 10,
  };
  const data = await client.request(query, variables);
  return data.warehouses as {
    data: IWarehouse[];
    page: number;
    size: number;
    total: number;
  };
};

export const getWarehouse = async (warehouseId: string) => {
  const query = `
    query($warehouseId: ID!) {
      warehouse(id: $warehouseId) {
        id
        name
        email
        warehouseCode
        mobileNumber
        totalStockValue
        address {
          street
          area
          city
          country
          zip
        }
      }
    }
  `;

  const variables = { warehouseId };
  const data = await client.request(query, variables);
  return data.warehouse as IWarehouse;
};

export const getWarehousesDropdown = async () => {
  const query = gql`
    query WarehouseDropdown {
      warehouseDropdown {
        id
        name
      }
    }
  `;

  const data = await client.request(query);
  return data.warehouseDropdown as IWarehouseDropDown[];
};

export const getWarehousesExport = async () => {
  const query = gql`
    query WarehouseDropdown {
      warehouseDropdown {
        id
        name
        email
        warehouseCode
        mobileNumber
        totalStockValue
        address {
          street
          area
          city
          country
          zip
        }
      }
    }
  `;

  const data = await client.request(query);
  return data.warehouseDropdown as IWarehouse[];
};

export type IImportWarehouseData = {
  name: string;
  email: string;
  warehouseCode: string;
  mobileNumber: string;
  address: {
    street: string;
    area: string;
    city: string;
    country: string;
    zip: string;
  };
};

export const importWarehouse = async (warehouses: IImportWarehouseData[]) => {
  const mutation = `
    mutation ($input: createInputCSVBulkWarehouseInput) {
      importCSVWarehouse(input: $input) {
        id
      }
    }
  `;

  const variables = {
    input: { data: warehouses },
  };
  const createdData = await client.request(mutation, variables);
  return createdData;
};

export const checkWarehouseCodeUnique = async (code: string) => {
  const query = gql`
    query ($warehouseCode: String) {
      isWarehouseCodeUnique(warehouseCode: $warehouseCode)
    }
  `;

  const variables = {
    warehouseCode: code,
  };
  const data = await client.request(query, variables);
  return data.isWarehouseCodeUnique as boolean;
};

export const checkWarehouseNameUnique = async (name: string) => {
  const query = gql`
    query Query($name: String) {
      isWarehouseNameUnique(name: $name)
    }
  `;

  const variables = {
    name,
  };
  const data = await client.request(query, variables);
  return data.isWarehouseNameUnique as boolean;
};
